// JS wrapper for the dedicated native-chat route-handoff protocol
// (Android only). Modeled directly on src/plugins/NativeBottomNav.js's
// proven shape - availability check, never-throw helpers. See
// android/.../notifications/NativeChatRoutePlugin.java for the full
// request/accept/reject/release lease contract this wraps.

import { Capacitor } from "@capacitor/core";

function getNativeChatRoutePlugin() {
  try {
    if (typeof window === "undefined") {
      return null;
    }
    return window.Capacitor?.Plugins?.NativeChatRoute ?? null;
  } catch {
    return null;
  }
}

export function isNativeChatRouteAvailable() {
  return Capacitor.getPlatform() === "android" && !!getNativeChatRoutePlugin();
}

/**
 * The closed, non-sensitive rejection reasons native can return - mirrors
 * NativeChatRejectionReason.java exactly.
 */
export const NATIVE_CHAT_REJECTION_REASON = Object.freeze({
  FEATURE_DISABLED: "FEATURE_DISABLED",
  HOST_UNAVAILABLE: "HOST_UNAVAILABLE",
  SESSION_MISMATCH: "SESSION_MISMATCH",
  STALE_ROUTE: "STALE_ROUTE",
  INVALID_ROUTE: "INVALID_ROUTE",
  ACCESS_NOT_VERIFIED: "ACCESS_NOT_VERIFIED",
  CONTENT_OWNER_BUSY: "CONTENT_OWNER_BUSY",
  INTERNAL_UNAVAILABLE: "INTERNAL_UNAVAILABLE",
});

/**
 * Requests native chat content ownership for one relative route. Never
 * throws - a missing plugin, non-Android platform, or a genuinely thrown/
 * rejected call all resolve to a closed INTERNAL_UNAVAILABLE rejection,
 * so the caller (NativeChatRouteBoundary.js) always has a single uniform
 * shape to branch on: fall back to ChatRoomPage immediately.
 *
 * @param {{
 *   relativeRoute: string,
 *   shellSessionId: string,
 *   sessionGeneration: number,
 *   routeRevision: number,
 * }} request
 * @returns {Promise<{accepted: true, leaseId: string} | {accepted: false, reason: string}>}
 */
export async function requestNativeChatOwnership(request) {
  const plugin = getNativeChatRoutePlugin();
  if (!plugin || typeof plugin.requestNativeChatOwnership !== "function") {
    return { accepted: false, reason: NATIVE_CHAT_REJECTION_REASON.INTERNAL_UNAVAILABLE };
  }
  try {
    const result = await plugin.requestNativeChatOwnership(request);
    if (!result || typeof result !== "object") {
      return { accepted: false, reason: NATIVE_CHAT_REJECTION_REASON.INTERNAL_UNAVAILABLE };
    }
    if (result.accepted === true) {
      const leaseId = typeof result.leaseId === "string" ? result.leaseId : null;
      if (!leaseId) {
        // An "accepted" response with no usable lease id is nonsensical -
        // never trusted, same as a genuine failure.
        return { accepted: false, reason: NATIVE_CHAT_REJECTION_REASON.INTERNAL_UNAVAILABLE };
      }
      return { accepted: true, leaseId };
    }
    const reason =
      typeof result.reason === "string" && Object.prototype.hasOwnProperty.call(NATIVE_CHAT_REJECTION_REASON, result.reason)
        ? result.reason
        : NATIVE_CHAT_REJECTION_REASON.INTERNAL_UNAVAILABLE;
    return { accepted: false, reason };
  } catch {
    return { accepted: false, reason: NATIVE_CHAT_REJECTION_REASON.INTERNAL_UNAVAILABLE };
  }
}

/**
 * Best-effort, never throws - releases exactly the lease this caller was
 * given by a prior accepted requestNativeChatOwnership call. A stale/
 * already-superseded leaseId is always a safe no-op on the native side
 * (see NativeChatLeaseGate.java).
 *
 * @param {string} leaseId
 */
export async function releaseNativeChatOwnership(leaseId) {
  const plugin = getNativeChatRoutePlugin();
  if (!plugin || typeof plugin.releaseNativeChatOwnership !== "function" || !leaseId) {
    return;
  }
  try {
    await plugin.releaseNativeChatOwnership({ leaseId });
  } catch {
    // best-effort only
  }
}

/**
 * Subscribe to native's own "I ended a lease you didn't ask me to end"
 * nudge (today: only the Notification-Center mutual-exclusivity
 * transfer). Carries the exact leaseId that was released - the caller
 * (NativeChatRouteBoundary.js) MUST verify this matches the lease it
 * currently holds before treating it as its own: a stale nudge for an
 * already-superseded lease must never suppress/close the current one.
 *
 * @param {(payload: {leaseId: string|null}) => void} callback
 */
export function addNativeChatOwnershipReleasedListener(callback) {
  const plugin = getNativeChatRoutePlugin();
  if (!plugin || typeof plugin.addListener !== "function") {
    return { remove: async () => {} };
  }
  return plugin.addListener("nativeChatOwnershipReleasedByNative", (payload) => {
    callback({ leaseId: typeof payload?.leaseId === "string" ? payload.leaseId : null });
  });
}

/**
 * The closed set of native-origin navigation destinations a future
 * NativeChatController may request - mirrors
 * NativeChatNavigationDestination.java exactly. Never an arbitrary route
 * string/URL/JS - see NativeChatRoutePlugin.java's own doc comment for
 * the full closed schema this event carries.
 */
export const NATIVE_CHAT_DESTINATION = Object.freeze({
  OUTER_MESSAGES: "OUTER_MESSAGES",
  USER_PROFILE: "USER_PROFILE",
});

/**
 * Subscribe to native-origin navigation-intent events (Header Back/
 * Delete-success/Profile-tap, once a future controller wires them - not
 * reachable in this foundation phase, since no production caller emits
 * one yet). The RAW event is handed to the caller unfiltered/unvalidated
 * beyond basic shape-sanitization - NativeChatRouteBoundary.js is
 * responsible for the full leaseId/session/generation/revision/eventId
 * validation before ever acting on it (see that module's own doc comment).
 *
 * @param {(event: {
 *   eventId: string,
 *   leaseId: string,
 *   shellSessionId: string,
 *   sessionGeneration: number,
 *   routeRevision: number,
 *   destination: string,
 *   targetUid: string|null,
 * } | null) => void} callback
 */
export function addNativeChatNavigationRequestedListener(callback) {
  const plugin = getNativeChatRoutePlugin();
  if (!plugin || typeof plugin.addListener !== "function") {
    return { remove: async () => {} };
  }
  return plugin.addListener("nativeChatNavigationRequested", (payload) => {
    if (!payload || typeof payload !== "object") {
      callback(null);
      return;
    }
    callback({
      eventId: typeof payload.eventId === "string" ? payload.eventId : null,
      leaseId: typeof payload.leaseId === "string" ? payload.leaseId : null,
      shellSessionId: typeof payload.shellSessionId === "string" ? payload.shellSessionId : null,
      sessionGeneration: typeof payload.sessionGeneration === "number" ? payload.sessionGeneration : null,
      routeRevision: typeof payload.routeRevision === "number" ? payload.routeRevision : null,
      destination: typeof payload.destination === "string" ? payload.destination : null,
      targetUid: typeof payload.targetUid === "string" ? payload.targetUid : null,
    });
  });
}
